import { useState } from "react";
import { toast } from "sonner";
import { AppLayout } from "@/components/layout/AppLayout";
import { ListShell } from "@/components/shared/ListShell";
import { ListPagination } from "@/components/shared/ListPagination";
import { DataTable, type DataTableColumn } from "@/components/shared/DataTable";
import { Button } from "@/components/ui/button";
import { PermissionGate } from "@/components/rbac/PermissionGate";
import {
  type PlanChangeRequestStatus,
  type PlanChangeRequestSummary,
  useApprovePlanChangeRequest,
  usePlanChangeRequestList,
  useRejectPlanChangeRequest,
} from "@/hooks/usePlanChangeRequests";
import { formatCurrencyINR, formatDate } from "@/utils/format";
import { RejectPlanChangeDialog } from "./RejectPlanChangeDialog";

type TabKey = "pending" | "history";

const tabs: Array<{
  key: TabKey;
  label: string;
  status?: PlanChangeRequestStatus;
}> = [
  { key: "pending", label: "Pending", status: "PENDING" },
  { key: "history", label: "History", status: undefined },
];

const DEFAULT_PAGE_SIZE = 25;

const directionFor = (
  current: PlanChangeRequestSummary["currentPlan"],
  target: PlanChangeRequestSummary["targetPlan"],
): { label: string; cls: string } => {
  if (!current) return { label: "New", cls: "bg-primary/10 text-primary" };
  const curPrice = Number(current.priceMonthly);
  const tgtPrice = Number(target.priceMonthly);
  if (current.id === target.id)
    return {
      label: "Reactivate",
      cls: "bg-warning/10 text-warning",
    };
  if (tgtPrice > curPrice)
    return { label: "Upgrade", cls: "bg-success/10 text-success" };
  if (tgtPrice < curPrice)
    return { label: "Downgrade", cls: "bg-destructive/10 text-destructive" };
  return { label: "Switch", cls: "bg-muted text-muted-foreground" };
};

export const PlanChangeRequestsList = () => {
  const [tab, setTab] = useState<TabKey>("pending");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const [rejectTarget, setRejectTarget] =
    useState<PlanChangeRequestSummary | null>(null);
  const statusFilter = tabs.find((t) => t.key === tab)?.status;

  const { data, isLoading, error, refetch } = usePlanChangeRequestList({
    status: statusFilter,
    page,
    limit,
  });
  const approve = useApprovePlanChangeRequest();
  const reject = useRejectPlanChangeRequest();

  const rows =
    tab === "history"
      ? (data?.data ?? []).filter((r) => r.status !== "PENDING")
      : (data?.data ?? []);
  const pagination = data?.pagination;

  const handleApprove = async (req: PlanChangeRequestSummary) => {
    try {
      await approve.mutateAsync({ id: req.id });
      toast.success("Plan change approved — subscription updated.");
      refetch();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const statusBadge = (status: PlanChangeRequestStatus) => {
    const cls =
      status === "PENDING"
        ? "bg-warning/10 text-warning"
        : status === "APPROVED"
          ? "bg-success/10 text-success"
          : status === "REJECTED"
            ? "bg-destructive/10 text-destructive"
            : "bg-muted text-muted-foreground";
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cls}`}>
        {status.toLowerCase()}
      </span>
    );
  };

  const columns: Array<DataTableColumn<PlanChangeRequestSummary>> = [
    {
      key: "owner",
      header: "Owner",
      render: (r) => (
        <>
          <div className="font-medium">{r.owner.name ?? "—"}</div>
          <div className="text-xs text-muted-foreground truncate max-w-xs">
            {r.owner.phone ?? r.owner.email ?? ""}
          </div>
        </>
      ),
    },
    {
      key: "change",
      header: "Change",
      render: (r) => {
        const dir = directionFor(r.currentPlan, r.targetPlan);
        return (
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <span className="text-sm">
                {r.currentPlan?.name ?? "—"} → <b>{r.targetPlan.name}</b>
              </span>
              <span
                className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${dir.cls}`}
              >
                {dir.label}
              </span>
            </div>
            <div className="text-xs text-muted-foreground">
              {r.currentPlan
                ? `${formatCurrencyINR(r.currentPlan.priceMonthly)} → `
                : ""}
              {formatCurrencyINR(r.targetPlan.priceMonthly)} / mo
            </div>
          </div>
        );
      },
    },
    {
      key: "subStatus",
      header: "Sub status",
      render: (r) => {
        const sub = r.owner.subscriptions[0];
        if (!sub)
          return <span className="text-xs text-muted-foreground">—</span>;
        return (
          <span className="text-xs text-muted-foreground">
            {sub.status.toLowerCase()}
          </span>
        );
      },
    },
    {
      key: "note",
      header: "Note",
      render: (r) => (
        <span className="text-xs text-muted-foreground line-clamp-2 max-w-xs">
          {r.note?.trim() || "—"}
        </span>
      ),
    },
    { key: "status", header: "Status", render: (r) => statusBadge(r.status) },
    {
      key: "submitted",
      header: "Submitted",
      render: (r) => (
        <span className="text-muted-foreground text-xs">
          {formatDate(r.createdAt)}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (r) =>
        r.status === "PENDING" ? (
          <div className="flex gap-2 justify-end">
            <PermissionGate module="subscriptions" action="edit">
              <Button
                size="sm"
                variant="outline"
                disabled={reject.isPending}
                onClick={() => setRejectTarget(r)}
              >
                Reject
              </Button>
              <Button
                size="sm"
                disabled={approve.isPending}
                onClick={() => handleApprove(r)}
              >
                Approve
              </Button>
            </PermissionGate>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">
            {r.rejectionReason || (r.reviewer?.name ?? "")}
          </span>
        ),
    },
  ];

  return (
    <AppLayout title="Plan change requests">
      <div className="flex gap-2 mb-4">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => {
              setTab(t.key);
              setPage(1);
            }}
            className={`px-3 py-1.5 text-sm rounded-btn border ${
              tab === t.key
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background border-border text-muted-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <ListShell
        isLoading={isLoading}
        error={error}
        isEmpty={rows.length === 0}
        onRetry={refetch}
        emptyTitle={
          tab === "pending"
            ? "No pending plan change requests"
            : "No plan change history"
        }
      >
        <DataTable columns={columns} rows={rows} rowKey={(r) => r.id} />
        <ListPagination
          pagination={pagination}
          page={page}
          rowsShown={rows.length}
          itemLabel="requests"
          onPageChange={setPage}
          limit={limit}
          onLimitChange={(n) => {
            setLimit(n);
            setPage(1);
          }}
        />
      </ListShell>

      <RejectPlanChangeDialog
        request={rejectTarget}
        onClose={() => setRejectTarget(null)}
        onRejected={() => {
          setRejectTarget(null);
          refetch();
        }}
      />
    </AppLayout>
  );
};

export default PlanChangeRequestsList;
